import { cx } from "../utils/cx";

/** Shimmer block. Size it with classes: `<Skeleton className="h-4 w-32" />`. */
export const Skeleton = ({ className }: { className?: string }) => (
    <span aria-hidden="true" className={cx("block animate-pulse rounded-md bg-quaternary/70", className)} />
);

export const SkeletonText = ({ lines = 3, className }: { lines?: number; className?: string }) => (
    <span className={cx("flex flex-col gap-2", className)} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
            <Skeleton key={i} className={cx("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")} />
        ))}
    </span>
);

/** Card-shaped placeholder (title, big number, two lines). */
export const SkeletonCard = ({ className }: { className?: string }) => (
    <div className={cx("flex flex-col gap-4 rounded-2xl bg-primary p-5 shadow-xs ring-1 ring-secondary ring-inset md:p-6", className)} aria-hidden="true">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-8 w-44" />
        <SkeletonText lines={2} />
    </div>
);

/** Table placeholder: header + rows. */
export const SkeletonTable = ({ rows = 5, columns = 4, className }: { rows?: number; columns?: number; className?: string }) => (
    <div className={cx("overflow-hidden rounded-2xl bg-primary shadow-xs ring-1 ring-secondary ring-inset", className)} aria-hidden="true">
        <div className="flex items-center gap-4 border-b border-secondary px-6 py-5">
            <Skeleton className="h-5 w-40" />
        </div>
        {Array.from({ length: rows }, (_, r) => (
            <div key={r} className="flex items-center gap-6 border-b border-secondary px-6 py-4 last:border-b-0">
                {Array.from({ length: columns }, (_, c) => (
                    <Skeleton key={c} className={cx("h-4", c === 0 ? "w-1/3" : "w-20", c > 1 && "max-md:hidden")} />
                ))}
            </div>
        ))}
    </div>
);

/** Whole-page placeholder: header, three cards, a table. Used while a page's first query loads. */
export const PageSkeleton = ({ cards = 3, header = true }: { cards?: number; /** Draw the title placeholder (off when a real PageHeader is already shown). */ header?: boolean }) => (
    <div className="flex flex-col gap-8" role="status" aria-live="polite">
        <span className="sr-only">در حال بارگذاری…</span>
        {header && (
            <div className="flex flex-col gap-2 px-4 lg:px-8">
                <Skeleton className="h-7 w-56" />
                <Skeleton className="h-4 w-80 max-w-full" />
            </div>
        )}
        {cards > 0 && (
            <div className="grid grid-cols-1 gap-4 px-4 md:grid-cols-3 lg:px-8">
                {Array.from({ length: cards }, (_, i) => (
                    <SkeletonCard key={i} />
                ))}
            </div>
        )}
        <div className="px-4 lg:px-8">
            <SkeletonTable />
        </div>
    </div>
);
